import React, { FC, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Alert, Button, Col, Modal, ModalBody, Row } from "reactstrap";
import { Icon } from "../../../../components/Component";
import FormSelect from "../../../form-components/SelectComponent";
import FormInput from "../../../form-components/InputComponent";
import FormTextArea from "../../../form-components/TextAreaComponent";
import FormInputSwitch from "../../../form-components/FormInputSwitch";
import { nanoid } from "@reduxjs/toolkit";
import workCategoryApi from "../../../../api/master/workCategory";
import sitebreakdownMasterApis from "../../../../api/master/sitebreakdownMaster";

const NewWorkMaterialModal: FC<any> = ({
  modal,
  closeModal,
  append,
  update,
  formData,
  materialsList,
  siteId,
  deliveryAddressType,
  materialCategoryOptions,
  source,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const methods = useForm({
    mode: "onChange",
  });

  // Prevent Reactstrap Modal's focus trap from stealing focus
  // when clicking Ant Design Select dropdown options (portaled to body)
  useEffect(() => {
    if (!modal) return;
    const handleFocusin = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target?.closest?.(".ant-select-dropdown")) {
        e.stopImmediatePropagation();
      }
    };
    document.addEventListener("focusin", handleFocusin, true);
    return () => document.removeEventListener("focusin", handleFocusin, true);
  }, [modal]);

  const {
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = methods;

  const [alertInfo, setAlertInfo] = useState({
    type: "",
    message: "",
    errors: [] as any[],
  });

  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
  const [uomOptions, setUomOptions] = useState<any[]>([]);

  const fetchWorkCategories = async () => {
    try {
      const res = await workCategoryApi.fetchWorkCategories();
      if (res?.status && res?.data) {
        const options = res.data.map((item: any) => ({
          label: item.categoryName || item.category || item.name,
          value: item.categoryId?.toString() || item.id?.toString(),
        }));
        setCategoryOptions(options);
      }
    } catch (error) {
      console.log("Error fetching work categories:", error);
    }
  };

  const fetchWorkUomList = async () => {
    try {
      const res = await sitebreakdownMasterApis.workUom({});
      if (res?.status && res?.data) {
        const options = res.data.map((item: any) => ({
          label: item.uomType || item.uom_name || item.name,
          value: item.workUomId?.toString() || item.id?.toString(),
        }));
        setUomOptions(options);
      }
    } catch (error) {
      console.log("Error fetching work UOM:", error);
    }
  };

  useEffect(() => {
    fetchWorkCategories();
    fetchWorkUomList();
  }, []);

  useEffect(() => {
    if (!alertInfo.message) return;
    const timeoutId = setTimeout(() => {
      setAlertInfo({
        type: "",
        message: "",
        errors: [],
      });
    }, 2500);
    return () => clearTimeout(timeoutId);
  }, [alertInfo]);

  useEffect(() => {
    if (formData?.action === "edit" && formData?.item) {
      const item = formData.item;
      setValue("taskTitle", item.taskTitle || "");
      setValue("description", item.description || "");
      setValue("workCategoryId", item.workCategoryId?.toString() || "");
      setValue("quantity", item.quantity);
      setValue("uomId", item.uomId?.toString() || "");
      setValue("taskUsage", item.taskUsage || "");
      setValue("installationRequired", item.installationRequired || false);
    }
  }, [formData, setValue]);

  const submitForm = (data: any) => {
    const { taskTitle, description, workCategoryId, quantity, uomId, taskUsage, installationRequired } = data;

    const categoryLabel =
      categoryOptions.find((item) => item.value === workCategoryId)?.label ||
      "";
    const uomLabel =
      uomOptions.find((item) => item.value === uomId)?.label || "";

    const lineData = {
      key: nanoid(),
      taskTitle,
      description: description || "",
      workType: "Open",
      workCategoryId: Number(workCategoryId) || 0,
      workCategoryName: categoryLabel,
      quantity: Number(quantity),
      uomId: Number(uomId),
      uomName: uomLabel,
      taskUsage: taskUsage || "",
      installationRequired: installationRequired || false,
    };

    if (formData.action === "add" || formData.index == null) {
      append(lineData);
    } else {
      update(formData.index, {
        ...formData.item,
        ...lineData,
      });
    }
    closeModal();
  };

  return (
    <Modal isOpen={modal} toggle={closeModal} size="xl" backdrop="static">
      <ModalBody>
        <div ref={modalRef}>
          <h5 className="title">
            {formData.action == "add" || formData.index == null
              ? "Add New Line"
              : "Update Line"}
          </h5>
          <div style={{ marginTop: "15px" }}></div>
          {alertInfo.message && (
            <div className="mb-3 mt-1">
              <Alert
                color={alertInfo.type === "error" ? "danger" : "success"}
                className="alert-icon"
              >
                <strong>{alertInfo.message}</strong>
                <Icon
                  name={
                    alertInfo.type === "error"
                      ? "alert-circle"
                      : "check-circle"
                  }
                />
                {alertInfo.errors.length > 0 ? (
                  <ul>
                    {alertInfo.errors.map((err: any, index: any) => (
                      <li key={index}>{err}</li>
                    ))}
                  </ul>
                ) : (
                  ""
                )}
              </Alert>
            </div>
          )}
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(submitForm)}>
              <Row>
                <div className="col-md-4">
                  <FormInput
                    type="text"
                    required={true}
                    name="taskTitle"
                    label="Task Title"
                    placeholder="Enter Task Title"
                  />
                </div>
                <div className="col-md-4">
                  <FormSelect
                    name="workCategoryId"
                    label="Work Category"
                    required={false}
                    options={categoryOptions}
                    placeholder="Select Work Category"
                    getPopupContainer={() =>
                      modalRef.current || document.body
                    }
                  />
                </div>
                <div className="col-md-4">
                  <FormInput
                    name="taskUsage"
                    label="Task Usage"
                    required={true}
                    type="text"
                    placeholder="Enter Task Usage"
                    tooltipText="Enter the usage of the task."
                  />
                </div>
              </Row>
              <div style={{ marginTop: "15px" }}></div>
              <Row>
                <div className="col-md-2">
                  <FormInput
                    type="number"
                    min={0.1}
                    required={true}
                    name="quantity"
                    label="Quantity"
                    placeholder="Enter Qty"
                  />
                </div>
                <div className="col-md-2">
                  <FormSelect
                    name="uomId"
                    label="UOM"
                    required={true}
                    options={uomOptions}
                    placeholder="Units"
                    getPopupContainer={() =>
                      modalRef.current || document.body
                    }
                  />
                </div>
                <div className="col-md-4">
                  <FormInputSwitch
                    name="installationRequired"
                    label="Installation Required"
                    optionLabel={watch("installationRequired") ? "Yes" : "No"}
                  />
                </div>
              </Row>
              <div style={{ marginTop: "15px" }}></div>
              <Row>
                <div className="col-md-12">
                  <FormTextArea
                    name="description"
                    label="Description"
                    required={false}
                    placeholder="Enter description (optional)"
                  />
                </div>
              </Row>
              <Row className="mt-4">
                <Col lg="12">
                  <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                    {formData.action === "edit" && formData.index != null && (
                      <li>
                        <Button
                          className="btn btn-primary btn-md"
                          type="submit"
                          color="primary"
                        >
                          Update
                        </Button>
                      </li>
                    )}
                    {(formData.action == "add" || formData.index == null) && (
                      <li>
                        <Button
                          className="btn btn-primary btn-md"
                          type="submit"
                          color="primary"
                        >
                          Save
                        </Button>
                      </li>
                    )}
                    <li>
                      <a
                        href="#cancel"
                        onClick={(ev) => {
                          ev.preventDefault();
                          closeModal();
                        }}
                        className="link link-light"
                      >
                        Cancel
                      </a>
                    </li>
                  </ul>
                </Col>
              </Row>
            </form>
          </FormProvider>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default NewWorkMaterialModal;
